import { createApp, ref, reactive, watchEffect, nextTick } from 'vue';
import { postBuyItem, getRealNameVerifyStatus } from '../pages/mulight-web/app/service';
import { isInApp, isDev } from '@/lib/utils';
import { openAppToast, openAppWebView, openAppSelectList, sendAppLog, openAppRechargePanel, openAppRealNameVerification, openAppVIPPanel } from './bridge';
import { fakeUserId } from '../config/config';
import AlertDialog from '@/components/AlertDialog';
import lottie from 'lottie-web';

const isInIMVApp = isInApp();
const isInDev = isDev();

export const showDialog = ({ title, content, buttonText }) => {
    const app = createApp({
        ...AlertDialog,
        data() {
            return {
                myTitle: title,
                myContent: content,
                myButtonText: buttonText,
            };
        },
        methods: {
            onClose() {
                app.unmount();
            },
        },
    });

    app.mount(document.createElement('div'));
}

/**
 * 使用lottie
 * @param {String} selector 容器选择器字符串
 * @param {String} animationData json数据
 * @returns [ lottie对象 ]
 */
export const useLottie = ({ selector, animationData }) => {
    const lottieWraps = document.querySelectorAll(selector);
    let history = null;
    if (lottieWraps?.length) {
        history = lottie.loadAnimation({
            container: lottieWraps[0],
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData,
        });
    }
    return [history];
};

/**
 * 导航渐变
 * @param {Number} navHeight - 导航区域像素高度 
 * @param {Number} contentTop - 内容区像素高度（到达此边缘完成渐变）
 * @param {String} titleBackgroundColorRGB - 导航最终变换背景色RGB值
 * @param {String} titleColorRGB - 导航最终变换文字色RGB值
 * @returns [ 包含titleBackgroundColor和titleColor的对象，延迟设置navHeight的函数， 延迟设置setContentTop的函数 ]
 */
export const useFadeNavBackground = ({ 
    navHeight, 
    contentTop, 
    titleBackgroundColorRGB = '137,101,255', 
    titleBackgroundColorMaxOpacity = 1,
    titleColorRGB = '255,255,255',
    showTips = true,
} = {}) => {

    const setNavHeight = (height) => {
        navHeight = height;
    };
    
    const setContentTop = (top) => {
        contentTop = top;
    };

    const setTitleBackgroundColorRGB = (color) => {
        titleBackgroundColorRGB = color;
        changeNavBackground();
    };
    
    const result = reactive({
        titleBackgroundColor: `rgba(${ titleBackgroundColorRGB }, 0)`,
        titleColor: `rgba(${ titleColorRGB }, 0)`,
    });
    
    const changeNavBackground = () => {
        if(!(navHeight && contentTop)){
            if(showTips){
                console.error('useFadeNavBackground请确保传递【navHeight】和【contentTop】参数');
            }
            return;
        }
        const scrollTop = (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop) + navHeight;
        // console.log(scrollTop, contentTop, navHeight)
        if (scrollTop < contentTop) {
            if (scrollTop >= contentTop - navHeight) {
                result.titleBackgroundColor = `rgba(${titleBackgroundColorRGB}, ${titleBackgroundColorMaxOpacity - (contentTop - scrollTop) / navHeight})`;
                result.titleColor = `rgba(${titleColorRGB}, ${1- (contentTop - scrollTop) / navHeight})`;
            } else {
                result.titleBackgroundColor = `rgba(${titleBackgroundColorRGB}, 0)`;
                result.titleColor = `rgba(${titleColorRGB}, 0)`;
            }
        } else {
            result.titleBackgroundColor = `rgba(${titleBackgroundColorRGB}, ${titleBackgroundColorMaxOpacity})`;
            result.titleColor = `rgba(${titleColorRGB}, 1)`;
        }
    };

    document.addEventListener('scroll', changeNavBackground, false);

    return [result, setNavHeight, setContentTop, setTitleBackgroundColorRGB];
};

/**
 * 导航滚动变化
 * @param {Number} navHeight - 导航区域像素高度 
 * @param {Number} changeScrollTop - 触发变化的高度
 * @returns [ 包含延迟设置navHeight的函数， 延迟设置changeScrollTop的函数 ]
 */
export const useNavScroll = ({ 
    navHeight, 
    changeScrollTop,
    beforeCallback,
    afterCallback,
    enable = false,
} = {}) => {
    const setNavHeight = (height) => {
        navHeight = height;
    };
    
    const setChangeScrollTop = (top) => {
        changeScrollTop = top;
    };

    const setEnable = (isEnable) => {
        enable = isEnable;
    };

    const changeEvent = () => {
        if(enable){
            if(!(navHeight !== undefined && changeScrollTop !== undefined)){
                console.error('useNavScroll请确保传递【navHeight】和【changeScrollTop】参数');
                return;
            }
            const scrollTop = (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop) + navHeight;
            // console.log(scrollTop, changeScrollTop, navHeight)
            if (scrollTop >= changeScrollTop) {
                afterCallback && afterCallback();
            } else {
                beforeCallback && beforeCallback();
            }
        }
    };

    document.addEventListener('scroll', changeEvent, false);

    return [setNavHeight, setChangeScrollTop, setEnable];
};

/**
 * 获取数据
 * @param {Function} fetchApi - 请求API service.js内定义 
 * @param {Object} params - 请求参数
 * @param {Boolean} isFetchImmediately - 是否立刻请求
 * @param {Boolean} isCustomErrorHandle - 是否自定义错误处理
 * @param {Number} fake_user_id - 只在测试环境接口有效果
 * @returns [ 请求结果，请求函数，加载状态，重置请求结果 ]
 */
 export const useFetchData = (fetchApi, params = {}, isFetchImmediately = false, isCustomErrorHandle = false, fake_user_id = isInDev ? fakeUserId : undefined) => {
    const result = reactive({});
    const loading = ref(false);
    
    const fetchData = async() => {
        loading.value = true;
        const res = await fetchApi({ ...params, fake_user_id }).catch();
        const msg = res?.error_msg || res?.message;
        // 前置拦截
        if(isInIMVApp && msg && (msg.indexOf('累计消费钻石已达限额') !== -1 || msg.indexOf('identityVerifyInfo') !== -1)){
            // Object.assign(result, res);
            if(msg.indexOf('identityVerifyInfo') !== -1){
                const messageJSON = JSON.parse(msg);
                if(messageJSON?.type === 1 && messageJSON?.identityVerifyInfo?.url){
                    if(messageJSON?.identityVerifyInfo?.status === 1){
                        openAppToast('实名认证审核中，请稍后再试');
                    }else{
                        openAppRealNameVerification(messageJSON.identityVerifyInfo.url);
                    }
                }
            }else{
                showDialog({ title: '暂时无法充值消费', content: msg || res?.message, buttonText: '知道啦' });
            }
        // 正常
        }else if (isCustomErrorHandle || res?.result === 1 || res?.status === 0) {
            Object.assign(result, res);
            // 正常请求把请求参数透传回去方便做局部修改
            result.params = params;
        // 异常
        }else{
            Object.assign(result, res);
            openAppToast(msg || '网络异常, 请稍后再试');
        }
        loading.value = false;
    };

    if (isFetchImmediately) {
        setTimeout(() => {
            fetchData();
        }, 0);
    }

    const doFetch = (newParams = {}, resetParams) => {
        if (newParams instanceof FormData) {
            params = newParams;
        } else {
            if (resetParams) {
                params = newParams;
            } else {
                params = { ...params, ...newParams }
            }
        }
        fetchData();
    };

    const resetResult = (resetValue) => {
        Object.keys(result).forEach(key => delete result[key]);
        if(resetValue){
            Object.assign(result, resetValue);
        }
    };

    return [result, doFetch, loading, resetResult];
};

/**
 * 异步单向调用（不会阻塞），会使用fake_user_id，但如果出错不会弹toast
 * @param {Function} fetchApi - 请求API service.js内定义
 * @param {Object} params - 请求参数
 * @param {Number} fake_user_id - 只在测试环境接口有效果
 * @returns [ 无 ]
 */
export const asyncQuietCall = (fetchApi, params = {}, fake_user_id = isInDev ? fakeUserId : undefined) => {
    try {
        fetchApi({ ...params, fake_user_id });
    } catch (e) {
        // ignore it
    }
}

/**
 * 商城
 * @param {String} userHeadUrl - 预览用的用户头像 
 * @param {Function} buySuccessCallback - 购买成功回调函数
 */
export const useShop = ({ userHeadUrl, userName, buySuccessCallback, buyErrorCallback }) => {
    // 点击购买商品
    const clickBuy = (data) => {
        // confirmData.value = data;
        closePreview();
        // isShowConfirm.value = true;
        buyItem(data);
    };

    // 预览
    const previewData = ref({});
    const isShowPreview = ref(false);
    const clickPreview = (data, groupName) => {
        previewData.value = { 
            ...data,
            groupName,
            userName: userName.value,
            userHeadUrl: userHeadUrl.value,
        };
        isShowPreview.value = true;
    };
    const closePreview = () => {
        isShowPreview.value = false;
    };

    // 确认弹窗
    const confirmData = ref({});
    const isShowConfirm = ref(false);
    const confirmBuy = () => {
        buyItem(confirmData.value);
        isShowConfirm.value = false;
    };
    const cancelBuy = () => {
        isShowConfirm.value = false;
    };

    // 购买流程
    const loading = ref(false);
    const [buyResult, buyItemFunc, buyLoading, resetBuyResult] = useFetchData(postBuyItem, {}, false, true);
    const buyItem = (data) => {
        if(loading.value){
            openAppToast('您点的太频繁啦，请稍后再试');
            return;
        }
        loading.value = true;
        resetBuyResult();
        buyItemFunc({ skuId: data.skuId });
    };

    watchEffect(() => {
        if(buyResult.result !== undefined){
            loading.value = false;
            if(buyResult.result === 1){
                openAppToast(`${buyResult?.data?.skuInfo?.name || ''} 购买成功, 可在“我的装扮”中查看`);
                sendAppLog({
                    type: 'SHOW',
                    action: 'BUY_DRESS_RESULT_POPUP',
                    data: {
                        result_type: 'success',
                        type: buyResult.data.skuInfo.payType ? 'diamond' : 'coin',
                    },
                });
                buySuccessCallback && buySuccessCallback(buyResult);
            }else{
                const msg = buyResult?.error_msg || buyResult?.message;
                if(msg && (msg.indexOf('累计消费钻石已达限额') !== -1 || msg.indexOf('identityVerifyInfo') !== -1)){
                    return;
                }
                const alertConfig = {};
                if(buyResult?.error_msg === '金币余额不足' || buyResult?.message === '金币余额不足'){
                    alertConfig.theme = 'yellow';
                    alertConfig.btnText = '赚金币';
                    alertConfig.content = '啊哦～ 金币余额不足';
                    alertConfig.isShow = true;
                    alertConfig.confirm = () => {
                        sendAppLog({
                            type: 'CLICK',
                            action: 'BUY_DRESS_NOT_ENOUGH_POPUP',
                            data: {
                                click_area: 'earn_coin',
                                type: 'coin',
                            },
                        });
                        sendAppLog({
                            type: 'CLICK',
                            action: 'EARN_COIN_BUTTON',
                            data: {
                                pos: 'buy_dress_not_enough',
                            },
                        });
                        openAppWebView(`${location.origin}/html/mulight-web/app/task/newTask/index.html?__titlebar=0&from=buy_dress_not_enough`);
                    };
                    sendAppLog({
                        type: 'SHOW',
                        action: 'BUY_DRESS_NOT_ENOUGH_POPUP',
                        data: {
                            type: 'coin',
                        },
                    });
                    sendAppLog({
                        type: 'SHOW',
                        action: 'EARN_COIN_BUTTON',
                        data: {
                            pos: 'buy_dress_not_enough',
                        },
                    });
                    buyErrorCallback && buyErrorCallback(alertConfig, buyResult)
                    return;
                }
                if(buyResult?.error_msg === '钻石余额不足' || buyResult?.message === '钻石余额不足'){
                    alertConfig.theme = 'purple';
                    alertConfig.btnText = '去充值';
                    alertConfig.content = '啊哦～ 钻石余额不足';
                    alertConfig.isShow = true;
                    alertConfig.confirm = () => {
                        sendAppLog({
                            type: 'CLICK',
                            action: 'BUY_DRESS_NOT_ENOUGH_POPUP',
                            data: {
                                click_area: 'recharge',
                                type: 'diamond',
                            },
                        });
                        sendAppLog({
                            type: 'CLICK',
                            action: 'RECHARGE_ENTRANCE_BUTTON',
                            data: {
                                pos: 'buy_dress_not_enough',
                            },
                        });
                        // openAppWebView(`${location.origin}/html/mulight-web/app/myDiamonds/index.html?__titlebar=0&from=buy_dress_not_enough`);
                        openAppRechargePanel('buy_dress_not_enough');
                    };
                    sendAppLog({
                        type: 'SHOW',
                        action: 'BUY_DRESS_NOT_ENOUGH_POPUP',
                        data: {
                            type: 'diamond',
                        },
                    });

                    sendAppLog({
                        type: 'SHOW',
                        action: 'RECHARGE_ENTRANCE_BUTTON',
                        data: {
                            pos: 'buy_dress_not_enough',
                        },
                    });
                    buyErrorCallback && buyErrorCallback(alertConfig, buyResult)
                    return;
                }
        
                openAppToast(buyResult?.error_msg || buyResult?.message || '网络异常, 请稍后再试');
            }
        }
    });

    // 点击参与活动
    const clickJoin = (item) => {
        // const jumpInfo = item.jumpInfo;
        // if(jumpInfo.length <= 1){
            // const jump = jumpInfo[0];
            // if(jump && jump.url){
                // if(jump.url.slice(0, 4) === 'http'){
                if(item?.jumpUrl?.slice(0, 4) === 'http'){
                    openAppWebView(item.jumpUrl);
                }else{
                    location.href = item.jumpUrl;
                }
            // }else{
                // openAppToast(item.grantToast);
            // }
        // }else{
        //     openAppSelectList(item.grantToast, jumpInfo, '取消', item);
        // }
    };

    // 点击Vip专属
    const clickVip = () => {
        openAppVIPPanel(true, () => {
            buySuccessCallback();
        });
    };

    return {
        clickBuy,
        isShowPreview,
        previewData,
        clickPreview,
        closePreview,
        isShowConfirm,
        confirmBuy,
        confirmData,
        cancelBuy,
        buyLoading: loading,
        clickJoin,
        clickVip,
    };
};

/**
 * 曝光埋点
 * tips: 曝光元素需求加上.j-init
 * @returns [ 初始化监听事件，监听元素 ]
 */
 export const useIntersectionObserver = () => {
    const elementObserver = ref();
    // 如果监听过早就先缓存起来 等事件绑定好了再执行
    const cache = [];
    const listenObserver = (itemClass) => {
        if (elementObserver.value) {
            nextTick(() => {
                const widgetItem = document.querySelectorAll(`${itemClass}.j-init`);
                if(widgetItem?.length){
                    [...widgetItem].forEach((item) => {
                        item.classList.remove('j-init');
                        elementObserver.value.observe(item);
                    });
                }
            });
        }else{
            cache.push(itemClass);
        }
    };
    const bindScreenShowEvent = (showCallback, proxy) => {
        if (window.IntersectionObserver) {
            elementObserver.value = new IntersectionObserver((entries) => {
                entries.forEach((item) => {
                    if (item.intersectionRatio > 0) {
                        const data = item.target.dataset;
                        elementObserver.value.unobserve(item.target);
                        showCallback && showCallback(data, item);
                    }
                });
            }, {
                thresholds: [1],
            });

            if(cache.length){
                cache.forEach((item) => {
                    listenObserver(item);
                });
                cache.length = 0;
            }
        }else{
            proxy?.$weblog?.show('no_support_observer');
            console.log('不支持observer');
        }
    };

    return [bindScreenShowEvent, listenObserver];
};

// 实名认证
export const useRealNameVerification = (successCallback, param = { type: 0 }) => {
    const [realNameData, realNameFunc] = useFetchData(getRealNameVerifyStatus, { biz: 'IMV_WALLET', ...param });
    watchEffect(() => {
        if(realNameData?.result === 1){
            // 实名成功
            if(realNameData.data.status === 2){
                successCallback && successCallback(realNameData.data);
            // 审核中
            }else if(realNameData.data.status === 1){
                openAppToast('实名认证审核中，请稍后再试');
            // 未实名/实名失败
            }else{
                openAppRealNameVerification(realNameData?.data?.url);
            }
        }
    });
    return [realNameFunc];
}

// 弹层控制
export const useDialogControl = (options = {}) => {
    const config = reactive({
        open: false,
        show: () => {
            config.open = true;
            options.showCallback && options.showCallback();
        },
        close: () => {
            config.open = false;
            options.closeCallback && options.closeCallback();
        },
        ...options,
    });
    return [config];
};

// 当前时间
export const useCurrentTime = () => {
    const initTime = ref(Date.now());
    const config = reactive({
        getInitTime: () => {
            return initTime.value;
        },
        getGap: (targetTimestamp = Date.now()) => {
            return targetTimestamp - initTime.value;
        },
    });
    return [config];
};

// tab控制
export const useTab = (options = {}) => {
    const tab = reactive({
        currentTab: options.initValue,
        // tab组件循环可选项 例如 [{ label: '周榜', value: 'week }, { label: '总榜', value: 'total‘ }]
        options: options?.options ?? [],
        changeTab: (tabName, changeCallback) => {
            if(options.preCheck && !options.preCheck(tabName)){
                return;
            }
            if(tabName !== tab.currentTab){
                tab.currentTab = tabName;
                options.changeCallback && options.changeCallback();
                changeCallback && changeCallback();
            }
        }
    });
    return [tab];
};

// 定时控制
export const useTimeStep = (options = {}) => {
    const step = ref(options.initValue || 0);
    let intervalId;

    const startTimeStep = () => {
        if (!intervalId) {
            intervalId = setInterval(() => {
                step.value = step.value + 1;
            }, options.time || 3000);
        }
    };

    const stopTimeStep = () => {
        if (intervalId) {
            clearInterval(intervalId);
        }
    };
    return [step, startTimeStep, stopTimeStep];
};
